import { useAppStore } from "@hooks/useAppStore";
import type { AppConfigMasteryLevelsType } from "@memorang/constants/config/schema";
import { buildSkillProficiencyEstimates } from "@memorang/helpers/language-learning";
import { getReportSkills } from "@memorang/helpers/report";
import type { BaseItemSchema, MixedItemTag } from "@memorang/types";
import {
	hasProficiencyProgressUi,
	resolveSessionProficiencyTags,
} from "@memorang/ui/components/SessionReport/SessionReportQuestionTypeCard/proficiency-levels";
import type { SessionReportProficiencyEstimateView } from "@memorang/ui/components/SessionReport/Summary/SessionReportProficiencySection";
import { useAppConfig } from "@memorang/ui/contexts/AppConfigContext";
import { type LevelIconName, isLevelIconName } from "@memorang/ui/icon";

const FALLBACK_PROFICIENCY_ICON: LevelIconName = "circle-outline";

const resolveProficiencyIcon = (
	levelLabel: string,
	proficiencyLevels: AppConfigMasteryLevelsType | undefined,
): LevelIconName => {
	const normalizedLabel = levelLabel.toLowerCase();

	for (const level of proficiencyLevels ?? []) {
		if (
			level.alias.title.toLowerCase() === normalizedLabel &&
			isLevelIconName(level.icon)
		) {
			return level.icon;
		}
	}

	return FALLBACK_PROFICIENCY_ICON;
};

type Props = {
	isSummativeTest: boolean;
	tags: MixedItemTag[];
	topics: string[];
	sessionItems: BaseItemSchema[];
};

export const useSessionReportProficiencyEstimates = ({
	isSummativeTest,
	tags,
	topics,
	sessionItems,
}: Props): SessionReportProficiencyEstimateView[] | undefined => {
	const proficiencyTargetScore = useAppStore(
		(state) => state.app.proficiencyTargetScore,
	);
	const { proficiencyLevels } = useAppConfig();
	const showProficiencyEstimates = useAppStore(
		(s) => s.reportView?.options?.showProficiencyEstimates,
	);

	if (!isSummativeTest || !showProficiencyEstimates) {
		return undefined;
	}

	const hasProgressUi = hasProficiencyProgressUi({
		tags,
		sessionItems,
		proficiencyLevels,
	});
	if (!hasProgressUi) {
		return undefined;
	}

	const skillTags = getReportSkills(tags, topics);
	if (skillTags.length === 0) {
		return undefined;
	}

	const proficiencyTags = resolveSessionProficiencyTags({
		tags,
		sessionItems,
		proficiencyLevels,
	});
	if (proficiencyTags.length === 0) {
		return undefined;
	}

	const estimates = buildSkillProficiencyEstimates({
		skillTags,
		proficiencyTags,
		proficiencyTargetScore,
	});
	if (estimates.length === 0) {
		return undefined;
	}

	return estimates.map((estimate) => ({
		skillId: estimate.skillId,
		skillTitle: estimate.skillTitle,
		currentLevel: estimate.currentLevel,
		...(estimate.nextLevel ? { nextLevel: estimate.nextLevel } : {}),
		progress: estimate.progress,
		readyToAdvance: estimate.readyToAdvance,
		icon: resolveProficiencyIcon(estimate.currentLevel, proficiencyLevels),
	}));
};
